import { useState } from "react";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { track } from "@/lib/analytics";
import { sendWelcomeEmail, type WelcomeEmailSource } from "@/lib/welcomeEmail";

interface NewsletterFormProps {
  utm_medium: string;
  placeholder?: string;
  buttonLabel?: string;
}

const NewsletterForm = ({
  utm_medium,
  placeholder = "you@startup.com",
  buttonLabel = "Subscribe",
}: NewsletterFormProps) => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    setErrorMsg("");
    const { data, error } = await supabase.functions.invoke("beehiiv-subscribe", {
      body: {
        email,
        utm_source: "newsletter",
        utm_medium,
        send_welcome_email: true,
      },
    });
    if (error || (data as any)?.error) {
      console.error("newsletter error", error, data);
      setErrorMsg("Something went wrong. Please try again.");
      setStatus("error");
      return;
    }
    track("newsletter_subscribed", { utm_source: "newsletter" });
    const source: WelcomeEmailSource =
      utm_medium === "blog_empty_state" ? "blog_empty_state" : "newsletter_homepage";
    sendWelcomeEmail(email, source);
    setStatus("success");
  };

  if (status === "success") {
    return (
      <p
        role="status"
        aria-live="polite"
        className="text-base font-bold text-foreground text-center"
      >
        You're on the list ✓
      </p>
    );
  }

  const isLoading = status === "loading";

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-3 max-w-md mx-auto"
      noValidate
    >
      <div className="flex flex-col sm:flex-row gap-3">
        <label htmlFor={`newsletter-email-${utm_medium}`} className="sr-only">
          Email address
        </label>
        <input
          id={`newsletter-email-${utm_medium}`}
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={placeholder}
          disabled={isLoading}
          className="flex-1 brutal-border bg-background px-4 py-3 text-sm font-medium text-foreground placeholder:text-muted-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={isLoading}
          aria-busy={isLoading}
          className="inline-flex items-center justify-center gap-2 brutal-border brutal-shadow bg-primary text-primary-foreground font-bold px-6 py-3 text-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all rounded-md uppercase tracking-[0.08em] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isLoading && <Loader2 className="w-4 h-4 animate-spin" aria-hidden />}
          {isLoading ? "Subscribing…" : buttonLabel}
        </button>
      </div>
      {status === "error" && (
        <p role="alert" className="text-sm font-bold text-destructive text-center">
          {errorMsg}
        </p>
      )}
    </form>
  );
};

export default NewsletterForm;
